import { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Check, Star, Clock, Shield, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { treatmentBySlug } from '../data/treatments';
import { trackCategory } from '../lib/tracking';
import LeadForm from '../components/LeadForm';
import Layout from '../components/Layout';
import Seo from '../components/Seo';

export default function TreatmentLanding({ slug }) {
  const config = treatmentBySlug(slug);
  const [clinics, setClinics] = useState([]);

  useEffect(() => {
    if (!config) return;
    trackCategory(config.category);
    api.get('/clinics/public').then((r) => setClinics(r.data.clinics || [])).catch(() => {});
  }, [config]);

  if (!config) return <Navigate to="/" replace />;

  const canonical = `https://specialists.no/${config.slug}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: config.displayName,
    description: config.seoDescription,
    url: canonical,
    areaServed: 'NO',
  };

  return (
    <Layout>
      <Seo
        title={config.seoTitle}
        description={config.seoDescription}
        canonical={canonical}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section data-testid={`treatment-hero-${config.slug}`} className="relative overflow-hidden bg-[#0A1F44] text-white">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#3B82F6]/20 rounded-full blur-[120px] -translate-y-1/4 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#3B82F6]/10 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="grid lg:grid-cols-[1.25fr_1fr] gap-12 lg:gap-16 items-start">
            {/* Left */}
            <div className="anim-fade-up" style={{ animationDelay: '0.05s' }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#3B82F6]/40 bg-[#3B82F6]/10 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#3B82F6] mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] anim-pulse" />
                {config.displayName}
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight">
                {config.title}
              </h1>

              <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-xl">
                {config.subtitle}
              </p>

              <div className="mt-8 flex flex-wrap gap-x-7 gap-y-3 text-sm">
                {config.bullets.map((t) => (
                  <div key={t} className="flex items-center gap-2 text-white/80">
                    <Check size={16} className="text-[#3B82F6]" strokeWidth={2.5} />
                    {t}
                  </div>
                ))}
              </div>

              {/* Hero image */}
              <div className="mt-10 rounded-2xl overflow-hidden border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
                <img
                  src={config.image}
                  alt={`${config.displayName} – Specialists.no`}
                  className="w-full h-64 sm:h-80 object-cover"
                  loading="eager"
                />
              </div>

              <div className="mt-8 flex items-center gap-5 pt-6 border-t border-white/10">
                <div className="flex items-center gap-1.5">
                  {[1,2,3,4,5].map((i) => (<Star key={i} size={15} fill="#3B82F6" className="text-[#3B82F6]" />))}
                  <span className="ml-1 text-white font-semibold text-sm">4.8</span>
                </div>
                <div className="h-4 w-px bg-white/15"></div>
                <div className="text-sm text-white/60">Over 2 000 pasienter har fått hjelp</div>
              </div>
            </div>

            {/* Right: form */}
            <div id="lead-form" className="anim-fade-up" style={{ animationDelay: '0.2s' }}>
              <LeadForm
                initialCategory={config.category}
                initialTreatment={config.treatment}
                lockedTreatment
                ctaText={config.cta}
                clinics={clinics}
              />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B82F6] mb-3">SLIK FUNGERER DET</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0A1F44] leading-tight">
              Tre enkle steg til {config.displayName.toLowerCase()}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { n: '01', t: 'Send forespørsel', d: `Fyll ut skjemaet for ${config.displayName.toLowerCase()}. Tar under ett minutt.` },
              { n: '02', t: 'Vi matcher klinikk', d: 'Du får en klinikk med 4+ stjerner som passer ditt behov og by.' },
              { n: '03', t: 'Klinikken ringer deg', d: 'En spesialist kontakter deg innen 24 timer og avtaler konsultasjon.' },
            ].map((s) => (
              <div key={s.n} className="relative">
                <div className="font-display text-7xl font-bold text-[#0A1F44]/8 leading-none absolute -top-6 -left-2 select-none -z-0">{s.n}</div>
                <div className="relative pt-10">
                  <h3 className="font-display text-2xl font-bold text-[#0A1F44] mb-2">{s.t}</h3>
                  <p className="text-[#64748B] leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-[#FAF8F5] py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { icon: Star, t: '4+ Stjerner', d: 'Kun klinikker med dokumentert høy rating på Google eller Trustpilot.' },
              { icon: Clock, t: 'Svar innen 24 timer', d: 'Klinikken forplikter seg til rask oppfølging via telefon eller e-post.' },
              { icon: Shield, t: 'GDPR-trygt', d: 'Kun delt med klinikken du selv valgte. Slettes etter 12 måneder.' },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="flex flex-col bg-white rounded-2xl p-8 border border-[#0A1F44]/10">
                <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center mb-5">
                  <Icon size={22} className="text-[#3B82F6]" strokeWidth={1.8} />
                </div>
                <h3 className="font-display text-2xl font-bold text-[#0A1F44] mb-2">{t}</h3>
                <p className="text-[#64748B] leading-relaxed">{d}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/klinikker"
              className="inline-flex items-center gap-2 text-[#0A1F44] font-semibold hover:text-[#3B82F6] transition-colors"
            >
              Se alle våre partnerklinikker <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
